import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import {reinitFeefo} from '../../custom-api/hooks/use-feefo'
import {Global, css} from '@emotion/react'

const ReviewScoreIcon = ({
    id,
    parentSku,
    bvData = 'inline_rating',
    variant = {},
    onVisibilityChange,
}) => {
    const stylesGlobal = useMultiStyleConfig('ReviewScoreIcon', variant)
    const [isVisible, setIsVisible] = useState(false)
    const bvRef = useRef(null)
    const feefoRef = useRef(null)
    const normalizedParentSku = parentSku?.toUpperCase().replace(/[^A-Z0-9_: ]/g, '')

    useEffect(() => {
        reinitFeefo()
    }, [id, parentSku])

    useEffect(() => {
        const bvElement = bvRef.current
        const feefoElement = feefoRef.current

        if (!bvElement && !feefoElement) return

        const updateVisibility = () => {
            const bvHeight = bvElement?.getBoundingClientRect().height ?? 0
            const feefoHeight = feefoElement?.getBoundingClientRect().height ?? 0
            const nextVisibility = bvHeight > 0 || feefoHeight > 0
            setIsVisible(nextVisibility)
            onVisibilityChange?.(nextVisibility)
        }

        const mutationObserver = new MutationObserver(updateVisibility)
        const observeOptions = {
            childList: true,
            subtree: true,
            attributes: true,
        }

        if (bvElement) mutationObserver.observe(bvElement, observeOptions)
        if (feefoElement) mutationObserver.observe(feefoElement, observeOptions)

        const timeouts = [0, 300, 1000, 3000].map((delay) => setTimeout(updateVisibility, delay))

        return () => {
            timeouts.forEach(clearTimeout)
            mutationObserver.disconnect()
        }
    }, [id, parentSku, bvData, onVisibilityChange])

    return (
        <>
            <Global
                styles={css`
                    ${stylesGlobal.global}
                `}
            />
            <Box
                {...stylesGlobal.container}
                position={isVisible ? undefined : 'absolute'}
                visibility={isVisible ? undefined : 'hidden'}
                pointerEvents={isVisible ? undefined : 'none'}
                height={isVisible ? undefined : '0'}
                overflow={isVisible ? undefined : 'hidden'}
            >
                {(id || normalizedParentSku) && (
                    <>
                        {id && (
                            <div
                                ref={bvRef}
                                data-bv-show={bvData}
                                data-bv-product-id={id.replace(/\s+/g, '_').replace(/[:+/]/g, '_')}
                            ></div>
                        )}
                        {normalizedParentSku && (
                            <div
                                className="feefo-product-stars-widget"
                                ref={feefoRef}
                                data-parent-product-sku={normalizedParentSku}
                                style={{pointerEvents: 'none'}}
                            ></div>
                        )}
                    </>
                )}
            </Box>
        </>
    )
}

ReviewScoreIcon.propTypes = {
    id: PropTypes.string,
    parentSku: PropTypes.string,
    bvData: PropTypes.string,
    onVisibilityChange: PropTypes.func,
    shouldRender: PropTypes.string,
    variant: PropTypes.object,
}

export default ReviewScoreIcon
